/**
 * アイテム定義レジストリ（自前・GPL非依存）。
 *
 * ここに 1 アイテムを 1 行で追加するだけで、全システムが整合する設計。
 *   - サーバー側（party/relay.ts）は **同じデータの手動コピー** を持つ（party は app の strict tsconfig 対象外で
 *     独自ビルドのため import 不可・運用上の重複コストは小さく、データ規模が小さいので二重定義で許容）。
 *   - クライアントは effects を読んで装備時の挙動（黒シャフト・ブレイクパワー）を駆動する。
 *
 * 既存アイテムを **削除しない**（保有していたユーザーから消えるバグの原因）。仕様変更は別 id で追加する。
 */

import type { InventoryItem, ItemId } from './types.js';

/** プロジェクトで使用する全アイテム（id → 定義）。
 *  追加方法: 末尾に 1 レコード追加するだけ。サーバー側 party/relay.ts の UNLOCK_RULES も同期更新する。 */
export const ITEM_REGISTRY: Readonly<Record<ItemId, InventoryItem>> = {
  'carbon-cue': {
    id: 'carbon-cue',
    category: 'cue',
    name: 'カーボンキュー',
    // 取得条件はここに書かない（未取得時の表示は unlock 条件から動的整形される hint が担う＝
    // main.ts formatUnlockHint。CPU 表示名のハードコードを避ける: no-hardcoded-context.md）。
    description: 'シャフトが黒くなり、ブレイクショットの最大スピードが上昇する。',
    effects: {
      shaftColor: '#1a1a1a',
      // 既定ブレイク上限 35 km/h ≒ 9.7 m/s に対し 40 km/h ≒ 11.11 m/s。
      // 装備時のみ上書き（未装備時は engine 既定）。
      breakShotMaxSpeed: 11.11,
    },
    unlock: { type: 'cpu-defeat', cpuId: 'cpu-efren' },
  },
  // ── 対戦中の自動照準スキル（段階解禁）。装備した1つのみ発動し、解除で停止する。
  //    解禁条件の cpuId はデータ（unlock）として持つ＝ロジックにハードコードしない
  //    （no-hardcoded-context.md 準拠）。許容カット角はデータ駆動（effects の度数）。
  'auto-aim-skill-1': {
    id: 'auto-aim-skill-1',
    category: 'skill',
    name: 'オートエイム Lv.1',
    description: '対戦中でもカット角 30° までの球をポケットへ自動照準するスキル。',
    effects: { autoAimMatchMaxCutDeg: 30 },
    unlock: { type: 'cpu-defeat-race', cpuId: 'cpu-efren', minTargetWins: 3 },
  },
  'auto-aim-skill-2': {
    id: 'auto-aim-skill-2',
    category: 'skill',
    name: 'オートエイム Lv.2',
    description: '対戦中でもカット角 50° までの球をポケットへ自動照準するスキル。',
    effects: { autoAimMatchMaxCutDeg: 50 },
    unlock: { type: 'cpu-defeat-race', cpuId: 'cpu-efren', minTargetWins: 5 },
  },
  'auto-aim-skill-max': {
    id: 'auto-aim-skill-max',
    category: 'skill',
    name: 'オートエイム Lv.MAX',
    description: '対戦中でもカット角 75° までの球をポケットへ自動照準するスキル。',
    effects: { autoAimMatchMaxCutDeg: 75 },
    unlock: { type: 'cpu-defeat-race', cpuId: 'cpu-efren', minTargetWins: 9 },
  },
  // Magician: 9ラックを全マスワリ（Perfect Game）で撃破。Lv.MAX 自動照準＋予測軌跡ガイド。
  magician: {
    id: 'magician',
    category: 'skill',
    name: 'Magician',
    description: '対戦中の自動照準（75°）に加え、予測軌跡ガイドを表示する最上位スキル。',
    effects: { autoAimMatchMaxCutDeg: 75, showAimGuideInMatch: true },
    unlock: { type: 'cpu-defeat-perfect', cpuId: 'cpu-efren', minTargetWins: 9 },
  },
  // ── ドリル級「完全クリア（全ドリルをノーミス取り切り）」報酬（F-30）。
  //    autoAimMatchMaxCutDeg を流用（Efren 報酬スキルと同一フィールド＝二重実装回避）。
  //    ドリル内オートエイムは controls 側で「保有スキルの最大カット角」まで有効化される（owned ベース）。
  'drill-aim-c': {
    id: 'drill-aim-c',
    category: 'skill',
    name: 'オートエイム Lv.C級',
    description: 'カット角 5° までの球をポケットへ自動照準するスキル（ドリル/対戦で有効）。',
    effects: { autoAimMatchMaxCutDeg: 5 },
    unlock: { type: 'drill-rank-no-miss', rank: 'C', minCount: 10 },
  },
  // ブレイク専用キュー（C級報酬）。装着でブレイク時の最大ヘッドスピードを 33km/h ≒ 9.17 m/s に。
  'drill-break-cue': {
    id: 'drill-break-cue',
    category: 'cue',
    name: 'ドリルブレイクキュー',
    description: '装着するとブレイクショットの最大スピードが上昇する（最大 33km/h）。',
    effects: { breakShotMaxSpeed: 9.17 },
    unlock: { type: 'drill-rank-no-miss', rank: 'C', minCount: 10 },
  },
  'drill-aim-b': {
    id: 'drill-aim-b',
    category: 'skill',
    name: 'オートエイム Lv.B級',
    description: 'カット角 10° までの球をポケットへ自動照準するスキル（ドリル/対戦で有効）。',
    effects: { autoAimMatchMaxCutDeg: 10 },
    unlock: { type: 'drill-rank-no-miss', rank: 'B', minCount: 10 },
  },
  'drill-aim-sb': {
    id: 'drill-aim-sb',
    category: 'skill',
    name: 'オートエイム Lv.SB級',
    description: 'カット角 12° までの球をポケットへ自動照準するスキル（ドリル/対戦で有効）。',
    effects: { autoAimMatchMaxCutDeg: 12 },
    unlock: { type: 'drill-rank-no-miss', rank: 'SB', minCount: 10 },
  },
  'drill-aim-a': {
    id: 'drill-aim-a',
    category: 'skill',
    name: 'オートエイム Lv.A級',
    description: 'カット角 15° までの球をポケットへ自動照準するスキル（ドリル/対戦で有効）。',
    effects: { autoAimMatchMaxCutDeg: 15 },
    unlock: { type: 'drill-rank-no-miss', rank: 'A', minCount: 10 },
  },
  'drill-aim-sa': {
    id: 'drill-aim-sa',
    category: 'skill',
    name: 'オートエイム Lv.SA級',
    description: 'カット角 20° までの球をポケットへ自動照準するスキル（ドリル/対戦で有効）。',
    effects: { autoAimMatchMaxCutDeg: 20 },
    unlock: { type: 'drill-rank-no-miss', rank: 'SA', minCount: 10 },
  },
};

/** 全アイテム ID 一覧（管理人特権で "全所持" 扱いに使う）。 */
export const ALL_ITEM_IDS: readonly ItemId[] = Object.freeze(Object.keys(ITEM_REGISTRY));

/** アイテム定義を取得（未定義は null）。 */
export const getItem = (id: ItemId): InventoryItem | null =>
  Object.prototype.hasOwnProperty.call(ITEM_REGISTRY, id) ? ITEM_REGISTRY[id] ?? null : null;
